import React, { lazy, type ComponentType } from 'react';

// React.lazy that retries chunk-load failures up to N times before bubbling.
// Common scenario: user has stale tab open during a deploy → old chunk URL 404s.
// On final failure, force a full reload to fetch the new index.html.
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  retries = 2,
  delayMs = 500,
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    let lastError: unknown;
    for (let i = 0; i <= retries; i++) {
      try {
        return await factory();
      } catch (err) {
        lastError = err;
        // ChunkLoadError or network error
        if (i < retries) {
          await new Promise((r) => setTimeout(r, delayMs * Math.pow(2, i)));
        }
      }
    }
    // All retries failed — likely stale build. Reload once (idempotent via session marker).
    if (typeof window !== 'undefined' && !sessionStorage.getItem('chunk-reloaded')) {
      sessionStorage.setItem('chunk-reloaded', '1');
      window.location.reload();
    }
    throw lastError;
  });
}
